/**
 * The engine renderer's handle on the mix runtime, without importing it.
 *
 * The vidOS engine draws every page, mixer or not; the mixer (mixRuntime.ts and everything it pulls in) loads lazily,
 * after the engine. So the engine renderer (and the main view) reach the runtime through this module-level slot,
 * filled by mixRuntime.ts when it first loads: until then the slot is empty and each call costs one null check.
 * Type-only imports keep this file free of the mixer's code.
 */

import type { ShaderUniformInfo } from '@/utils/shaderParser';

import type { MixChannelPlayhead, MixChannelSound, MixEnginePlayhead } from './mixRuntimeTypes';
import type { Mix } from './mixTypes';

/** Where the engine runs a channel's knobs: its MIDI channel (1-16) and each knob's CC, by knob name. */
export interface MixEngineKnobSlots {
  midiChannel: number;
  ccs: Readonly<Record<string, number>>;
}

/** What the engine renderer calls on the mix runtime. */
export interface RendererMixRuntime {
  appliedMix(): Mix;
  /** The app's transport (the engine's frame passes it on): the channels' files and music follow it. */
  setPlaying(playing: boolean): void;
  /** The main view is covered or the page hidden: the channels' files pause. */
  setHidden(hidden: boolean): void;
  /** The engine's knobs for a channel the runtime has none for (a Sampler.frag video or image); null drops them. */
  setEngineUniforms(channelId: string, info: ShaderUniformInfo | null, amountKnob?: string | null): void;
  /** The engine's clip playheads (sui_mix_playheads); null: it stopped. */
  setEnginePlayheads(playheads: ReadonlyArray<MixEnginePlayhead | null> | null): void;
  /**
   * The engine compiled a mix shader's `code` (error null) or could not (`error`: why, e.g. `line 3: ...`): the
   * channels and FX running that code say so in their views.
   */
  noteEngineCompile(code: string, error: string | null): void;
  /** Where each video or audio channel's file plays (the mixer's seek bars). */
  playheads(): MixChannelPlayhead[];
  /** Where each channel's music plays. */
  musicPlayheads(): MixChannelPlayhead[];
  /** How loud each of a channel's sounds plays now (its meter's and knob modulation's). */
  soundNow(channelId: string): MixChannelSound;
  /** Whether a channel's video or audio file has sound of its own. */
  fileHasAudio(channelId: string): boolean;
  /** The engine's knob slots for a channel (its MIDI channel and each knob's CC: sui_mix_knob_slots); null drops them. */
  setEngineKnobSlots(channelId: string, slots: MixEngineKnobSlots | null): void;
}

let runtime: RendererMixRuntime | null = null;
/** The main view's last word, for a runtime that loads after it. */
let hidden = false;

/** The mix runtime, once the mixer has loaded; null before (and on Ratchet Sheet). */
export function loadedMixRuntime(): RendererMixRuntime | null {
  return runtime;
}

/** mixRuntime.ts, as it loads: from now on the engine renderer drives it. */
export function installMixRuntime(next: RendererMixRuntime): void {
  if (runtime === next) return;
  runtime = next;
  next.setHidden(hidden);
}

/** The main view is covered or hidden (forwarded once the runtime loads). */
export function setMixRendererHidden(next: boolean): void {
  hidden = next;
  runtime?.setHidden(next);
}
